import React from 'react';
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, MessageCircle, Heart } from 'lucide-react';
import { BRAND } from '../data/mock';
import { useQuote } from '../context/QuoteContext';

const FooterLink = ({ href, children, testid }) => (
  <a href={href} className="text-stone-400 hover:text-white transition-colors text-sm" data-testid={testid}>{children}</a>
);

const Footer = () => {
  const { openQuote } = useQuote();
  return (
    <footer className="relative bg-stone-950 text-stone-300 pt-16 pb-8 mt-8">
      {/* Blush divider strip */}
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-pink-brand via-pink-300 to-pink-brand" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Brand column */}
          <div>
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="Mr. Coconetto" className="w-12 h-12 rounded-full bg-white/5 p-1 object-contain" />
              <div>
                <div className="font-script text-pink-300 text-lg -mb-1">Simply Original</div>
                <div className="font-display font-bold text-white text-lg">Mr. Coconetto</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-stone-400 leading-relaxed">
              India's #1 custom coconut brand. AI-engraved, personalized coconuts for weddings, corporate events &amp; every big day.
            </p>
            <div className="mt-5 flex items-center gap-2">
              {[Instagram, Facebook, Youtube].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full bg-white/5 hover:bg-pink-brand transition-colors flex items-center justify-center text-white">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <div>
            <div className="font-display font-semibold text-white mb-4">Explore</div>
            <div className="grid gap-2.5">
              <FooterLink href="#services" testid="footer-link-services">Services</FooterLink>
              <FooterLink href="#celebrities" testid="footer-link-celebrities">Celebrities</FooterLink>
              <FooterLink href="#plans" testid="footer-link-plans">Plans</FooterLink>
              <FooterLink href="#cities" testid="footer-link-cities">Cities</FooterLink>
              <FooterLink href="#testimonials" testid="footer-link-testimonials">Testimonials</FooterLink>
              <FooterLink href="#faqs" testid="footer-link-faqs">FAQs</FooterLink>
            </div>
          </div>

          {/* Contact */}
          <div>
            <div className="font-display font-semibold text-white mb-4">Contact</div>
            <div className="space-y-3 text-sm">
              <a href={`tel:${BRAND.phone}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone-link">
                <Phone className="w-4 h-4 text-pink-brand" /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email-link">
                <Mail className="w-4 h-4 text-pink-brand" /> {BRAND.email}
              </a>
              <a href={`https://wa.me/${BRAND.phoneRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-whatsapp-link">
                <MessageCircle className="w-4 h-4 text-pink-brand" /> WhatsApp Support
              </a>
              <div className="flex items-start gap-2 text-stone-400">
                <MapPin className="w-4 h-4 text-pink-brand mt-0.5 flex-shrink-0" />
                <span>Delhi (HQ) · Mumbai · Hyderabad<br />Pan-India delivery across 27+ cities</span>
              </div>
            </div>
          </div>

          {/* Quote CTA */}
          <div>
            <div className="font-display font-semibold text-white mb-4">Plan an Event?</div>
            <p className="text-sm text-stone-400 leading-relaxed">Get a personalised quote in under 24 hours.</p>
            <button
              type="button"
              onClick={() => openQuote({ source: 'footer' })}
              className="mt-5 w-full rounded-full bg-pink-brand hover:bg-[#BE185D] text-white font-semibold py-3 shadow-pink-glow"
              data-testid="footer-request-quote-button"
            >
              Request a Quote
            </button>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500">
          <div>© {new Date().getFullYear()} Mr. Coconetto. All rights reserved.</div>
          <div className="flex items-center gap-1.5">Made with <Heart className="w-3 h-3 text-pink-brand fill-current" /> in India</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
